// Pure logic for the A/B comparison view (docs/plan/phase-10-ui.md,
// "Compare"): the URL round trip for the pair being compared, the
// seek/frame-step/sync arithmetic that keeps two <video> elements showing
// the same frame while one of them is clipped away, and the per-side fact
// rows. Framework-free, like lib/library.ts and lib/results.ts, so each
// piece is unit-testable without rendering the view.
//
// Why the view exists: two encodes of the same shot are judged by eye, and
// judging them means seeing the same frame twice, in place, at the same
// size. A crf or a pixel format says little on its own -- what the eye is
// reacting to is what the encoder spent its bits on, which is why the
// average bitrate (the file's own size against the probed duration) leads
// each side's facts.
import type { ArtifactListItem } from "../api/client";
import { formatDuration, mediaDimensions, type MediaDetailRow } from "./library";
import { formatBytes } from "./results";

export const COMPARE_PATH = "/library/compare";

// ---- the pair --------------------------------------------------------------

export interface ComparePair {
  a: string;
  b: string;
}

/** The pair from `?a=&b=`: undefined unless both ids are present and
 * distinct -- an artifact compared against itself is not a comparison, and
 * a half-filled pair has nothing to show. */
export function comparePairFromSearchParams(params: URLSearchParams): ComparePair | undefined {
  const a = params.get("a")?.trim();
  const b = params.get("b")?.trim();
  if (!a || !b || a === b) return undefined;
  return { a, b };
}

export function compareSearchParams(pair: ComparePair): URLSearchParams {
  const params = new URLSearchParams();
  params.set("a", pair.a);
  params.set("b", pair.b);
  return params;
}

/** The comparator's own URL, so a comparison can be linked, bookmarked or
 * reloaded. The ids are the whole state; nothing else is in the query. */
export function compareHref(pair: ComparePair): string {
  return `${COMPARE_PATH}?${compareSearchParams(pair).toString()}`;
}

// ---- the divider -----------------------------------------------------------

export const DEFAULT_DIVIDER_PCT = 50;
// The handle stops short of either edge so the pinned side never collapses
// to nothing -- a comparison you cannot see is not a comparison.
export const MIN_DIVIDER_PCT = 5;
export const MAX_DIVIDER_PCT = 95;

export function clampDivider(percent: number): number {
  if (!Number.isFinite(percent)) return DEFAULT_DIVIDER_PCT;
  return Math.min(MAX_DIVIDER_PCT, Math.max(MIN_DIVIDER_PCT, percent));
}

// ---- playback --------------------------------------------------------------

/** The platform's own output rate; used when an artifact carries no probe
 * result (a shared link opened before Store has probed the bytes). */
export const DEFAULT_FPS = 24;
const MIN_FRAME_STEP_S = 1 / 240;
const MAX_FRAME_STEP_S = 1 / 2;

/** One frame, from the probed fps. A missing or nonsensical fps falls back
 * to DEFAULT_FPS, and the result is clamped so a bad probe value cannot
 * make a step invisible or turn it into a jump. */
export function frameStepSeconds(fps?: number): number {
  if (!fps || !Number.isFinite(fps) || fps <= 0) return 1 / DEFAULT_FPS;
  return Math.min(MAX_FRAME_STEP_S, Math.max(MIN_FRAME_STEP_S, 1 / fps));
}

/** `current` moved one frame in `direction`, held inside [0, duration]. A
 * duration the browser has not reported yet leaves the upper clamp off
 * rather than inventing one. */
export function steppedTime(
  current: number,
  direction: 1 | -1,
  fps: number | undefined,
  duration: number | undefined,
): number {
  const from = Number.isFinite(current) ? current : 0;
  const next = from + direction * frameStepSeconds(fps);
  if (next <= 0) return 0;
  if (duration !== undefined && Number.isFinite(duration) && duration > 0 && next > duration) {
    return duration;
  }
  return next;
}

// Two independent <video> elements do not stay on the same frame by
// themselves: one decoder's clock drifts against the other's. A tolerance
// well under a frame keeps the correction from fighting the decoder it is
// correcting -- re-seeking every timeupdate would stutter playback.
export const SYNC_TOLERANCE_S = 0.08;

/** Where the follower belongs to sit on the master's frame, or undefined
 * when it is already close enough to leave alone. */
export function driftCorrection(
  masterTime: number,
  followerTime: number,
  tolerance = SYNC_TOLERANCE_S,
): number | undefined {
  if (!Number.isFinite(masterTime) || !Number.isFinite(followerTime)) return undefined;
  return Math.abs(masterTime - followerTime) > tolerance ? masterTime : undefined;
}

// ---- the two sides' facts --------------------------------------------------

const BITS_PER_MEGABIT = 1_000_000;

/** Average bitrate over the whole clip. This is the number behind "B looks
 * better than A": crf does not say how many bits a particular shot
 * actually took, and the two encodes being compared differ in exactly
 * that. Undefined when either input is missing or unusable. */
export function averageBitrateBps(sizeBytes?: number, durationS?: number): number | undefined {
  if (!sizeBytes || !durationS || !Number.isFinite(durationS) || durationS <= 0) return undefined;
  return (sizeBytes * 8) / durationS;
}

export function formatBitrate(bps?: number): string | undefined {
  if (!bps || !Number.isFinite(bps)) return undefined;
  return `${(bps / BITS_PER_MEGABIT).toFixed(1)} Mbps`;
}

// The attrs that name the encoding itself, in the order they should read.
// Ordered rather than alphabetical because "variant, crf, pixel format" is
// how the two sides differ; the same keys sorted alphabetically bury the
// answer.
const ENCODING_ATTRS: Array<[key: string, label: string]> = [
  ["variant", "Variant"],
  ["crf", "CRF"],
  ["pix_fmt", "Pixel format"],
  ["profile", "Profile"],
  ["steps", "Steps"],
  ["precision", "Precision"],
  ["model", "Model"],
  ["runtime", "Runtime"],
];

export function encodingAttrs(item: ArtifactListItem): MediaDetailRow[] {
  const rows: MediaDetailRow[] = [];
  for (const [key, label] of ENCODING_ATTRS) {
    const value = item.attrs?.[key];
    if (value === undefined || value === null || value === "") continue;
    rows.push({ label, value: String(value) });
  }
  return rows;
}

/** Everything a viewer needs to tell one side from the other: the file
 * itself (size, average bitrate) first, then what the probe measured, then
 * the encoding attrs. A field neither source provides is simply absent,
 * the same convention the API and mediaDetailRows use. */
export function compareSideRows(item: ArtifactListItem): MediaDetailRow[] {
  const rows: MediaDetailRow[] = [];
  if (item.size) rows.push({ label: "Size", value: formatBytes(item.size) });
  const bitrate = formatBitrate(averageBitrateBps(item.size, item.media?.duration_s));
  if (bitrate) rows.push({ label: "Average bitrate", value: bitrate });
  const dims = mediaDimensions(item.media);
  if (dims) rows.push({ label: "Dimensions", value: dims });
  if (typeof item.media?.duration_s === "number") {
    rows.push({ label: "Duration", value: formatDuration(item.media.duration_s) });
  }
  if (typeof item.media?.fps === "number" && item.media.fps > 0) {
    rows.push({ label: "Frame rate", value: `${Math.round(item.media.fps * 100) / 100} fps` });
  }
  if (item.media?.vcodec) rows.push({ label: "Codec", value: item.media.vcodec });
  rows.push(...encodingAttrs(item));
  return rows;
}

/** The short name a side is known by. `attrs.variant` when the uploader set
 * one (the A/B/C of a bake-off), else the label, else the id -- never
 * nothing, so the two panels are always tellable apart. */
export function compareSideTitle(item: ArtifactListItem): string {
  const variant = item.attrs?.variant;
  if (variant !== undefined && variant !== null && String(variant) !== "") return String(variant);
  return item.label?.trim() || item.id.slice(0, 8);
}

/** The one-line description under a side's title: the facts that fit on a
 * line and are most likely to explain a visible difference -- pixel
 * format, crf, average bitrate, in that order. */
export function compareSideLine(item: ArtifactListItem): string {
  const parts: string[] = [];
  const pixFmt = item.attrs?.pix_fmt;
  if (pixFmt !== undefined && pixFmt !== null && String(pixFmt) !== "") parts.push(String(pixFmt));
  const crf = item.attrs?.crf;
  if (crf !== undefined && crf !== null && String(crf) !== "") parts.push(`crf ${crf}`);
  const bitrate = formatBitrate(averageBitrateBps(item.size, item.media?.duration_s));
  if (bitrate) parts.push(bitrate);
  return parts.join(" · ");
}
